/**
 * What an uploaded image becomes: the sizes stored for each kind, the limits on what is accepted, and the URL of each
 * stored size. The API generates from these and its presenters build URLs from them, so the two cannot disagree.
 * See docs/media.md.
 */

export const IMAGE_KINDS = ['logo', 'premiseGallery', 'activityGallery'] as const
export type ImageKind = (typeof IMAGE_KINDS)[number]

/** The most images one gallery holds - a premise's, or one activity's. */
export const GALLERY_LIMIT = 10

/** The largest file accepted, before any processing. */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024

/** The largest decoded image accepted. Stops a small file that decodes to a huge bitmap (a "decompression bomb"). */
export const MAX_INPUT_PIXELS = 40_000_000

/** The private master's longest side. It is kept so a new size can be generated later without a re-upload. */
export const MASTER_MAX_SIDE = 2560

/**
 * The widths stored per kind, smallest first. A logo is square, so its width is its height; a gallery image keeps
 * its aspect ratio and is scaled to the width.
 */
export const IMAGE_VARIANT_WIDTHS: Record<ImageKind, readonly number[]> = {
  logo: [128, 256, 512],
  premiseGallery: [480, 960, 1600],
  activityGallery: [480, 960, 1600],
}

/** The narrowest image accepted: the smallest stored size, which is never upscaled. */
export const minSourceWidth = (kind: ImageKind): number => IMAGE_VARIANT_WIDTHS[kind][0] ?? 0

/** Whether the kind is cropped to a square on the server, whatever shape arrives. */
export const isSquareKind = (kind: ImageKind): boolean => kind === 'logo'

/**
 * The widths to generate from a source this wide: every stored width the source can fill without upscaling. Empty
 * for a source narrower than `minSourceWidth`, which is refused.
 */
export function variantWidths(kind: ImageKind, sourceWidth: number): number[] {
  return IMAGE_VARIANT_WIDTHS[kind].filter((width) => width <= sourceWidth)
}

/** The object key of one stored size, under the image's `storageKey`. */
export const variantKey = (storageKey: string, width: number): string => `${storageKey}/${width}.webp`

/**
 * The width of the one JPEG kept for link previews (docs/media.md, _What is stored_): the stored sizes are WebP,
 * which some crawlers ignore. A logo's is its largest size; a gallery image's the 1200 px a share card is drawn at.
 */
export const SHARE_IMAGE_WIDTH: Record<ImageKind, number> = {
  logo: 512,
  premiseGallery: 1200,
  activityGallery: 1200,
}

/** The share copy's width for a stored image this wide: never upscaled. */
export const shareWidth = (kind: ImageKind, storedWidth: number): number =>
  Math.min(SHARE_IMAGE_WIDTH[kind], storedWidth)

/** The share copy's size: its width, and the stored image's aspect ratio. */
export function shareSize(image: { width: number; height: number; shareWidth: number }) {
  return { width: image.shareWidth, height: Math.round((image.height * image.shareWidth) / image.width) }
}

/** The object key of the JPEG share copy. Public, but never in a response: only link previews name it. */
export const shareKey = (storageKey: string): string => `${storageKey}/share.jpg`

/** The object key of the private master. Never public, never in a response. */
export const masterKey = (storageKey: string): string => `${storageKey}/master.webp`

/**
 * Where an image's objects live: one folder per image, so replacing an image never overwrites a cached object. Every
 * environment shares one Space, so each writes under its own `prefix` (`media/develop`, `media/production`, …) and
 * never touches another's files.
 */
export function imageStorageKey(prefix: string, entityId: string, imageId: string): string {
  const folder = prefix.replace(/^\/+|\/+$/g, '')
  return `${folder ? `${folder}/` : ''}entities/${entityId}/images/${imageId}`
}

export interface ImageVariantUrl {
  width: number
  url: string
}

/** The public URL of each stored size, smallest first. `baseUrl` is the CDN's, with or without a trailing slash. */
export function imageVariantUrls(baseUrl: string, storageKey: string, widths: readonly number[]): ImageVariantUrl[] {
  const base = baseUrl.replace(/\/+$/, '')
  return [...widths].sort((a, b) => a - b).map((width) => ({ width, url: `${base}/${variantKey(storageKey, width)}` }))
}

/**
 * The size to load for a slot `cssWidth` CSS pixels wide on a screen of `dpr`: the smallest that is at least as wide
 * as the slot's device pixels, else the largest there is. `undefined` only for an image with no sizes.
 */
export function pickVariant<V extends { width: number }>(
  variants: readonly V[],
  cssWidth: number,
  dpr = 1,
): V | undefined {
  const needed = cssWidth * dpr
  const sorted = [...variants].sort((a, b) => a.width - b.width)
  return sorted.find((variant) => variant.width >= needed) ?? sorted[sorted.length - 1]
}
